import { DirectionSelect, parseDirection, parseDirectionFilter } from '@/components/travel/direction';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  Filter,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Loader2,
  User,
  MoreHorizontal,
  Send,
  CreditCard,
  AlertCircle,
  FileText,
  DollarSign,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useSessionState } from '@/hooks/use-session-state';
import { InvoiceModal } from '@/components/travel/invoices/invoice-modal';
import { InvoicePaymentModal } from '@/components/travel/invoices/invoice-payment-modal';
import { usePermission } from '@/components/guards/permission-guard';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-purple-100 text-purple-700',
  PAID: 'bg-green-100 text-green-700',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-700',
  OVERDUE: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
  REFUNDED: 'bg-pink-100 text-pink-700',
};

const statusLabelKeys: Record<string, string> = {
  DRAFT: 'status.draft',
  SENT: 'status.sent',
  VIEWED: 'status.viewed',
  PAID: 'status.paid',
  PARTIALLY_PAID: 'status.partial',
  OVERDUE: 'status.overdue',
  CANCELLED: 'status.cancelled',
  REFUNDED: 'status.refunded',
};

const typeLabelKeys: Record<string, string> = {
  PROFORMA: 'type.proforma',
  INVOICE: 'type.invoice',
  CREDIT_NOTE: 'type.creditNote',
};

const typeColors: Record<string, string> = {
  PROFORMA: 'bg-yellow-100 text-yellow-700',
  INVOICE: 'bg-blue-100 text-blue-700',
  CREDIT_NOTE: 'bg-red-100 text-red-700',
};

type InvoiceStatus =
  | 'DRAFT'
  | 'SENT'
  | 'VIEWED'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'REFUNDED';
type InvoiceType = 'PROFORMA' | 'INVOICE' | 'CREDIT_NOTE';

export function InvoicesPage() {
  const { t, i18n } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const direction = parseDirectionFilter(searchParams.get('direction'));

  // RBAC permission checks
  const canCreate = usePermission('INVOICES', 'CREATE');
  const canEdit = usePermission('INVOICES', 'EDIT');
  // Delete available for future actions
  const _canDelete = usePermission('INVOICES', 'DELETE');
  void _canDelete;
  const [debouncedSearch, setDebouncedSearch] = useSessionState<string>(
    'travel:invoices:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useSessionState<InvoiceStatus | undefined>(
    'travel:invoices:statusFilter',
    undefined
  );
  const [typeFilter, setTypeFilter] = useSessionState<InvoiceType | undefined>(
    'travel:invoices:typeFilter',
    undefined
  );
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<{
    id: string;
    currency: string;
    balanceDue: number;
  } | null>(null);
  const limit = 10;
  useEffect(() => { setPage(1); }, [direction]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.invoice.list.useQuery({
    page,
    limit,
    search: debouncedSearch || undefined,
    status: statusFilter,
    type: typeFilter,
    direction,
  });

  const { data: stats } = trpc.invoice.stats.useQuery({ direction });

  const utils = trpc.useUtils();

  const updateStatusMutation = trpc.invoice.updateStatus.useMutation({
    onSuccess: () => {
      utils.invoice.list.invalidate();
      utils.invoice.stats.invalidate();
    },
  });

  const invoices = data?.invoices ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 0 };

  const handleStatusChange = (invoiceId: string, newStatus: InvoiceStatus) => {
    updateStatusMutation.mutate({ id: invoiceId, status: newStatus });
  };

  const handleInvoiceCreated = (invoiceId: string) => {
    navigate(`/travel/invoices/${invoiceId}`);
  };

  const handleRecordPayment = (invoice: { id: string; currency: string; balanceDue: number }) => {
    setSelectedInvoice(invoice);
    setIsPaymentModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {t('subtitle')}
            {pagination.total > 0 && ` (${pagination.total} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setIsInvoiceModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('createInvoice')}
          </Button>
        )}
      </div>

      <DirectionSelect filter value={direction} onChange={value => setSearchParams(previous => { const next = new URLSearchParams(previous); if(value) next.set('direction', value); else next.delete('direction'); return next; })} />

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="border-border bg-card border p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                <p className="text-muted-foreground text-sm">{t('stats.totalRevenue')}</p>
              </div>
            </div>
          </div>
          <div className="border-border bg-card border p-4">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-2">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalOutstanding)}</p>
                <p className="text-muted-foreground text-sm">{t('stats.outstanding')}</p>
              </div>
            </div>
          </div>
          <div className="border-border bg-card border p-4">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 p-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.overdue}</p>
                <p className="text-muted-foreground text-sm">{t('stats.overdue')}</p>
              </div>
            </div>
          </div>
          <div className="border-border bg-card border p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2">
                <Receipt className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.thisMonth}</p>
                <p className="text-muted-foreground text-sm">{t('stats.thisMonth')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Row */}
      {stats && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          <div className="border-border bg-card flex items-center gap-2 border px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-gray-400" />
            <span className="text-sm">
              {t('stats.draft')}: {stats.draft}
            </span>
          </div>
          <div className="border-border bg-card flex items-center gap-2 border px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-blue-400" />
            <span className="text-sm">
              {t('stats.sent')}: {stats.sent}
            </span>
          </div>
          <div className="border-border bg-card flex items-center gap-2 border px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-orange-400" />
            <span className="text-sm">
              {t('stats.partial')}: {stats.partial}
            </span>
          </div>
          <div className="border-border bg-card flex items-center gap-2 border px-4 py-2">
            <span className="h-2 w-2 rounded-full bg-green-400" />
            <span className="text-sm">
              {t('stats.paid')}: {stats.paid}
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            setStatusFilter((e.target.value as InvoiceStatus) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('allStatuses')}</option>
          <option value="DRAFT">{t('status.draft')}</option>
          <option value="SENT">{t('status.sent')}</option>
          <option value="VIEWED">{t('status.viewed')}</option>
          <option value="PARTIALLY_PAID">{t('status.partiallyPaid')}</option>
          <option value="PAID">{t('status.paid')}</option>
          <option value="OVERDUE">{t('status.overdue')}</option>
          <option value="CANCELLED">{t('status.cancelled')}</option>
          <option value="REFUNDED">{t('status.refunded')}</option>
        </select>
        <select
          value={typeFilter ?? ''}
          onChange={(e) => {
            setTypeFilter((e.target.value as InvoiceType) || undefined);
            setPage(1);
          }}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('allTypes')}</option>
          <option value="INVOICE">{t('type.invoice')}</option>
          <option value="PROFORMA">{t('type.proforma')}</option>
          <option value="CREDIT_NOTE">{t('type.creditNote')}</option>
        </select>
        <Button variant="outline">
          <Filter className="mr-2 h-4 w-4" />
          {t('moreFilters')}
        </Button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('loadingInvoices')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('errorLoading')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && invoices.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Receipt className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('noInvoicesFound')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {debouncedSearch || statusFilter || typeFilter
              ? t('adjustFilters')
              : t('invoicesWillAppear')}
          </p>
          {!debouncedSearch && !statusFilter && !typeFilter && canCreate && (
            <Button className="mt-4" onClick={() => setIsInvoiceModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('createInvoice')}
            </Button>
          )}
        </div>
      )}

      {/* Invoices Table */}
      {!isLoading && !error && invoices.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('table.invoiceNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('table.customer')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('table.type')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('table.issueDate')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('table.dueDate')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('table.status')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('table.amount')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/travel/invoices/${invoice.id}`}
                        className="text-primary font-medium hover:underline"
                      >
                        {invoice.invoiceNumber}
                      </Link>
                      {invoice.booking && (
                        <p className="text-muted-foreground text-xs">
                          {t('table.booking')}: {invoice.booking.bookingReference}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                          <User className="text-primary h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {invoice.customer?.firstName} {invoice.customer?.lastName}
                          </p>
                          <p className="text-muted-foreground text-xs">{invoice.customer?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium ${
                          typeColors[invoice.invoiceType] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {t(typeLabelKeys[invoice.invoiceType] || 'type.unknown')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(invoice.issueDate).toLocaleDateString(dateLocale)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {new Date(invoice.dueDate).toLocaleDateString(dateLocale)}
                      </span>
                      {invoice.isOverdue && (
                        <p className="flex items-center gap-1 text-xs text-red-500">
                          <AlertCircle className="h-3 w-3" />
                          {t('status.overdue')}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium ${
                          statusColors[invoice.status] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {t(statusLabelKeys[invoice.status] || 'status.unknown')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="font-medium">{formatCurrency(invoice.totalAmount)}</p>
                      {invoice.balanceDue > 0 && invoice.status !== 'PAID' && (
                        <p className="text-xs text-orange-600">
                          {t('amounts.due')}: {formatCurrency(invoice.balanceDue)}
                        </p>
                      )}
                      {invoice.amountPaid > 0 && invoice.status !== 'PAID' && (
                        <p className="text-xs text-green-600">
                          {t('amounts.paid')}: {formatCurrency(invoice.amountPaid)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/travel/invoices/${invoice.id}`}>
                              <FileText className="mr-2 h-4 w-4" />
                              {t('actions.viewDetails')}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {canEdit && invoice.status === 'DRAFT' && (
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(invoice.id, 'SENT')}
                            >
                              <Send className="mr-2 h-4 w-4" />
                              {t('actions.markAsSent')}
                            </DropdownMenuItem>
                          )}
                          {canEdit &&
                            (invoice.status === 'SENT' ||
                              invoice.status === 'PARTIALLY_PAID' ||
                              invoice.status === 'OVERDUE') && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleRecordPayment({
                                    id: invoice.id,
                                    currency: invoice.currency,
                                    balanceDue: invoice.balanceDue,
                                  })
                                }
                              >
                                <CreditCard className="mr-2 h-4 w-4" />
                                {t('actions.recordPayment')}
                              </DropdownMenuItem>
                            )}
                          {canEdit && invoice.status === 'SENT' && (
                            <DropdownMenuItem
                              onClick={() => handleStatusChange(invoice.id, 'PAID')}
                            >
                              <DollarSign className="mr-2 h-4 w-4" />
                              {t('actions.markAsPaid')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {t('pagination.showing', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('pagination.previous')}
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === pagination.page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className="w-8"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  {t('pagination.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {isInvoiceModalOpen && (
        <InvoiceModal
          defaultDirection={parseDirection(direction ?? null)}
          open={isInvoiceModalOpen}
          onOpenChange={setIsInvoiceModalOpen}
          onSuccess={handleInvoiceCreated}
        />
      )}

      {isPaymentModalOpen && selectedInvoice && (
        <InvoicePaymentModal
          open={isPaymentModalOpen}
          onOpenChange={setIsPaymentModalOpen}
          invoiceId={selectedInvoice.id}
          currency={selectedInvoice.currency}
          balanceDue={selectedInvoice.balanceDue}
          onSuccess={() => {
            utils.invoice.list.invalidate();
            utils.invoice.stats.invalidate();
            setSelectedInvoice(null);
          }}
        />
      )}
    </div>
  );
}
